import { computed, Injectable, signal } from '@angular/core';
import { GuideFaq } from '../models/guide.model';
import { GUIDE_FAQS } from '../registry/faqs';

export interface FaqCategoryGroup {
  category: string;
  faqs: GuideFaq[];
}

/**
 * Loads and exposes the list of {@link GuideFaq}s (the FAQ knowledge base).
 *
 * MVP: served from the static {@link GUIDE_FAQS} constant. Later this becomes
 * an HTTP-backed, admin-editable source — see the commented `loadRegistry()`
 * seam.
 */
@Injectable({ providedIn: 'root' })
export class FaqRegistryService {
  private readonly faqsSignal = signal<GuideFaq[]>(GUIDE_FAQS);

  /** All registered FAQ entries. */
  readonly faqs = this.faqsSignal.asReadonly();

  /** FAQs flagged for the empty-state General Questions list in the AI sidebar. */
  readonly featuredFaqs = computed(() =>
    this.faqsSignal().filter((faq) => faq.isFeatured),
  );

  /** Featured FAQs grouped by category for accordion display. */
  readonly featuredFaqsByCategory = computed((): FaqCategoryGroup[] => {
    const groups = new Map<string, GuideFaq[]>();
    for (const faq of this.featuredFaqs()) {
      const list = groups.get(faq.category) ?? [];
      list.push(faq);
      groups.set(faq.category, list);
    }
    return Array.from(groups.entries()).map(([category, faqs]) => ({
      category,
      faqs,
    }));
  });

  findFaq(faqId: string): GuideFaq | undefined {
    return this.faqsSignal().find((faq) => faq.id === faqId);
  }

  // SEAM (future backend pass): replace the static seed with a fetch.
  // loadRegistry(): Observable<GuideFaq[]> {
  //   return this.http.get<GuideFaq[]>(`${environment.shout_api.url}/guide/faqs`).pipe(
  //     tap((faqs) => this.faqsSignal.set(faqs)),
  //   );
  // }
}
